import React from "react";
import ChapterPathList from "./ChapterPathList";
import { useQuery } from "@tanstack/react-query";
import { getAllChapters, callWithAuth } from "../../api/Api";
import { GetAllChaptersRequest } from "@shared_types";
import Title from "../Title";
import { LoadingPage } from "@/pages/LoadingPage";
import ContinueButton from "../Buttons/ContinueButton";
import { useCurrentCourse } from "@/hooks/useSelectedCourse";
import { useNavigate } from "react-router-dom";
import { FLIGHT_SCHOOL_COURSE_ID } from "@/features/flight-school";
import FlightSchoolHomePath from "./FlightSchoolHomePath";

const CourseView: React.FC = () => {
  const { currentCourseID, isCurrentCourseLoading } = useCurrentCourse();
  const navigate = useNavigate();
  // Flight School is a virtual course (id -1) with no backend chapters; querying
  // getAllChapters(-1) returns nothing → the old "No course selected" break.
  // Render the FS module path instead (parity with iOS Home).
  const isFlightSchool = currentCourseID === FLIGHT_SCHOOL_COURSE_ID;

  const courseInfoQuery = useQuery({
    queryKey: ["courseInfo", currentCourseID],
    queryFn: () => {
      const data: GetAllChaptersRequest = {
        courseId: currentCourseID as number,
      };
      return callWithAuth(getAllChapters, data);
    },
    // No selected course → don't fetch (avoids the endless home spinner); fall
    // through to the "No course selected / Browse courses" empty state instead.
    // Flight School (-1) has no backend chapters — skip the doomed fetch.
    enabled: currentCourseID != null && !isFlightSchool,
  });

  const course = courseInfoQuery.data?.course;
  const chapters = courseInfoQuery.data?.chapters || [];
  const courseTitle = course?.title || "Select a course";

  // Only block on the spinner while we genuinely have a course to load. With no
  // selected course the query is disabled, so we must NOT spin on it.
  if (
    isCurrentCourseLoading ||
    (currentCourseID != null && courseInfoQuery.isLoading)
  )
    return (
      <div className="fixed inset-2 flex items-center justify-center">
        <LoadingPage showText={false} />
      </div>
    );

  // Flight School: render the module path instead of the (empty) course chapters.
  if (isFlightSchool) {
    return <FlightSchoolHomePath />;
  }

  return (
    <div
      className={`flex flex-col w-full justify-start overflow-scroll gap-4 no-scrollbar`}
    >
      <div className="w-full h-auto overflow-scroll z-0 no-scrollbar">
        {chapters.length > 0 ? (
          <ChapterPathList chapters={chapters} courseTitle={courseTitle} />
        ) : (
          <>
          <div className="flex flex-row items-center mb-4">
            <Title text={courseTitle} />
          </div>
          <div className="flex flex-col relative items-center justify-between w-full h-48 p-4 bg-white rounded-xl border border-[var(--color-grey-light)]">
            <div className="text-xl font-semibold text-[var(--color-grey)]">
              No course selected
            </div>
            <div className="text-base font-normal text-[var(--color-grey)]">
              Browse our courses to find the perfect one for you
            </div>
            <ContinueButton
              text="Browse courses"
              className="max-w-[300px] mt-10"
              onClick={() => {
                navigate("/home/courses");
              }}
            />
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CourseView;
